/**
 * "Find My Tools" quiz data and recommendation logic.
 *
 * The original quiz was decorative past the first click: every option in step 2
 * returned the same role it was reached from, so the second question could not
 * change the outcome. It only ever set a role filter on the grid.
 *
 * Step 2 now narrows to a specific intent, and each intent names the tools that
 * actually serve it. Three rules govern the mapping, and `quizData.test.ts`
 * enforces all of them:
 *
 *   1. Role consistency. Every tool recommended in a branch must carry that
 *      branch's role tag in toolsData. The quiz cannot contradict the site's
 *      own Think / Clarify / Create taxonomy.
 *   2. An approachable option every time. Every intent must recommend at least
 *      one Beginner-level tool. The audience is women 55+ who describe
 *      themselves as not technical; an intent that returns only Intermediate
 *      tools is a dead end for them.
 *   3. Coverage. Every published tool must be reachable from at least one
 *      intent, unless it is listed in EXCLUDED_FROM_RECOMMENDATIONS below.
 *      That list is empty today, so every tool is reachable.
 *
 * The "why" shown on a recommendation is the tool's own `bestFor` line from
 * toolsData, so no new marketing copy is invented here. Only the question
 * labels are new, and those are Caren's to edit.
 */

import { tools, type Role, type Tool } from "@/lib/toolsData";
import { ROLE_ICONS, ROLE_LABELS } from "@/lib/roleDisplay";

// Re-exported so the quiz's callers have one import, not two.
export { ROLE_ICONS, ROLE_LABELS };

export interface QuizIntent {
  /** Stable id, safe for analytics or a shareable result URL. */
  id: string;
  label: string;
  /** Tool ids, best fit first. Two or three. */
  toolIds: string[];
}

/**
 * Tools that stay in the directory but are never recommended by the quiz.
 *
 * Empty today, and deliberately kept as a mechanism rather than deleted.
 *
 * Manus AI was a candidate: the Manus platform Caren's site was hosted on shut
 * down on 22 Aug 2026, which would have taken its affiliate link with it.
 * Daniel confirmed the product itself stays open, so it is recommended
 * normally. If that changes, or if any other tool is discontinued, adding its
 * id here removes it from every recommendation without touching its copy, its
 * grid entry or its /resources/<id> URL.
 */
export const EXCLUDED_FROM_RECOMMENDATIONS: readonly string[] = [];

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  Atlas: "Research, plan, and organize your thinking with these tools.",
  Comet: "Get fast answers, summaries, and comparisons with these tools.",
  Manus: "Write, design, and produce content with these tools.",
};

export const QUESTION_ONE = "What are you trying to do right now?";
export const QUESTION_TWO = "More specifically?";

export const STEP_ONE_OPTIONS: { label: string; role: Role }[] = [
  { label: "I need to research or plan something", role: "Atlas" },
  { label: "I want a fast answer", role: "Comet" },
  { label: "I want to make something", role: "Manus" },
];

export const STEP_TWO_OPTIONS: Record<Role, QuizIntent[]> = {
  Atlas: [
    {
      id: "atlas-plan",
      label: "Plan a project or structure my thinking",
      // chatgpt leads on approachability, claude is the stronger tool for it.
      toolIds: ["chatgpt", "claude", "gemini"],
    },
    {
      id: "atlas-documents",
      label: "Make sense of my own documents and notes",
      toolIds: ["gemini-notebook", "claude", "littlebird"],
    },
    {
      id: "atlas-research",
      label: "Research a topic properly, with sources",
      toolIds: ["perplexity", "claude", "gemini-notebook"],
    },
  ],
  Comet: [
    {
      id: "comet-summarize",
      label: "Summarize something long",
      toolIds: ["perplexity", "gemini-notebook", "claude"],
    },
    {
      id: "comet-compare",
      label: "Compare my options before I decide",
      toolIds: ["perplexity", "chatgpt", "gemini"],
    },
    {
      id: "comet-current",
      label: "Find out what is happening right now",
      toolIds: ["perplexity", "gemini"],
    },
    {
      id: "comet-recall",
      label: "Find something I saw or heard but cannot locate",
      toolIds: ["littlebird", "gemini-notebook", "gemini"],
    },
  ],
  Manus: [
    {
      id: "manus-write",
      label: "Write something from scratch",
      toolIds: ["chatgpt", "claude", "gemini"],
    },
    {
      id: "manus-repurpose",
      label: "Turn a recording or long video into something shorter",
      toolIds: ["opus-clip", "castmagic", "agent-opus"],
    },
    {
      id: "manus-visual",
      label: "Make images or social graphics",
      // Canva led here until it moved to the Resource Center on 25 Aug 2026.
      // The quiz resolves ids against toolsData only, so it cannot point at a
      // resource; Gemini takes the slot because it generates images and is
      // Beginner-level, which is what rule 2 needs from this intent now that
      // Grok has been removed from the site and Gamma is the only other live
      // answer.
      toolIds: ["gemini", "gamma"],
    },
    {
      id: "manus-slides",
      label: "Build a presentation or a pitch deck",
      // Two answers, not three, since Canva left. Gamma is the deck builder and
      // Claude writes the content; nothing else in the catalogue makes slides.
      toolIds: ["gamma", "claude"],
    },
    {
      id: "manus-build-app",
      label: "Build a simple app or tool for my business",
      /* Added 3 Sep 2026 with the two tools themselves. Rule 2 is satisfied by
         Base44, which is the Beginner-level way in: it decides the database,
         the framework and the hosting for you, which is exactly the set of
         questions that stops this audience. Lovable follows because it is the
         same idea with the lid off. */
      toolIds: ["base44", "lovable"],
    },
    {
      id: "manus-automate",
      label: "Automate work that keeps repeating",
      // Manus AI is the only general automation tool in the catalogue and it
      // leads here. Castmagic follows because it is the Beginner-level way in:
      // it automates repurposing without asking anyone to design a workflow.
      toolIds: ["manus", "castmagic", "agent-opus"],
    },
  ],
};

/** Every intent, flattened, so a result can be looked up by id alone. */
export const ALL_INTENTS: QuizIntent[] = Object.values(STEP_TWO_OPTIONS).flat();

export const findIntent = (id: string): QuizIntent | undefined =>
  ALL_INTENTS.find(intent => intent.id === id);

/** The role branch an intent was reached from. */
export const roleForIntent = (id: string): Role | undefined =>
  (Object.keys(STEP_TWO_OPTIONS) as Role[]).find(role =>
    STEP_TWO_OPTIONS[role].some(intent => intent.id === id)
  );

/**
 * Resolve an intent to real Tool records.
 *
 * Unpublished entries are skipped rather than rendered, so setting
 * `isPublished: false` on a tool takes it out of recommendations too and does
 * not leave the quiz pointing at a card that no longer exists in the grid.
 * Unknown ids are skipped for the same reason.
 */
export function getRecommendations(intentId: string): Tool[] {
  const intent = findIntent(intentId);
  if (!intent) return [];
  return intent.toolIds
    .map(id => tools.find(tool => tool.id === id))
    .filter((tool): tool is Tool => tool !== undefined && tool.isPublished !== false);
}
